import { useState, useContext, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Stack,
  Typography,
  Box,
  Autocomplete,
  Collapse,
  Paper,
  FormControlLabel,
  Switch,
  Alert,
  Tabs,
  Tab,
  IconButton,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LinkIcon from "@mui/icons-material/Link";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { DataContext } from "../../contexts/DataProvider";
import { fetchAllContacts, createContact } from "../../services/contacts.service";
import { linkContact } from "../../services/relationships.service";
import { useAlert } from "../../functions/useAlert";
import { getContactConfig } from "../../utils/contactConfig";
import { updateContactGraph } from "../../functions/knowledgeGraphApi";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^(\+61[0-9]{9}|0[0-9]{9}|\+?[0-9]{10,15})$/;

// ── Types ─────────────────────────────────────────────────────────────────────

interface LinkContactModalProps {
  open: boolean;
  onClose: () => void;
  /** Called after a contact is successfully linked or created+linked */
  onSave: () => void;
  clientId: string;
  clientName: string;
  /** Used to filter relationship type options */
  entityType?: string;
  /** Contacts already linked — excluded from the "Link Existing" list */
  existingLinkedContacts?: any[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const emptyNewContact = (defaultRelationship: string) => ({
  firstName: "",
  surname: "",
  email: "",
  phone: "",
  relationshipType: defaultRelationship,
  isPrimary: false,
});

// ── Component ─────────────────────────────────────────────────────────────────

export default function LinkContactModal({
  open,
  onClose,
  onSave,
  clientId,
  clientName,
  entityType,
  existingLinkedContacts = [],
}: LinkContactModalProps) {
  const { login } = useContext(DataContext);
  const [setAlert] = useAlert();
  const config = getContactConfig(entityType);

  const [tab, setTab] = useState<"link" | "create">("link");
  const [loading, setLoading] = useState(false);

  // ── "Link Existing" tab state ─────────────────────────────────────────────
  const [allContacts, setAllContacts] = useState<any[]>([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [selectedContact, setSelectedContact] = useState<any>(null);
  const [linkForm, setLinkForm] = useState({ relationshipType: "", isPrimary: false });
  const [linkErrors, setLinkErrors] = useState<Record<string, string>>({});

  // Primary-swap confirmation
  const [confirmSwapOpen, setConfirmSwapOpen] = useState(false);
  const [existingPrimaryName, setExistingPrimaryName] = useState("");

  // ── "Create New" tab state ────────────────────────────────────────────────
  const [newContact, setNewContact] = useState(emptyNewContact(config.defaultRelationship));
  const [newErrors, setNewErrors] = useState<Record<string, string>>({});
  const [createError, setCreateError] = useState<string | null>(null);

  // ── Reset on open/entityType change ──────────────────────────────────────
  useEffect(() => {
    if (open) {
      setTab("link");
      setSelectedContact(null);
      setLinkForm({ relationshipType: "", isPrimary: existingLinkedContacts.length === 0 });
      setLinkErrors({});
      // Mirror the "Link Existing" isPrimary logic: default to true when this
      // will be the client's first contact, so the user doesn't have to
      // manually toggle it on the "Create New" tab.
      setNewContact({ ...emptyNewContact(config.defaultRelationship), isPrimary: existingLinkedContacts.length === 0 });
      setNewErrors({});
      setCreateError(null);
      loadContacts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // When entity type changes, reset relationship defaults
  useEffect(() => {
    setLinkForm((prev) => ({ ...prev, relationshipType: "" }));
    setNewContact((prev) => ({ ...prev, relationshipType: config.defaultRelationship }));
  }, [entityType]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadContacts = async () => {
    setLoadingContacts(true);
    try {
      const resp: any = await fetchAllContacts();
      const contacts = (resp.contacts || []).sort(
        (a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setAllContacts(contacts);
    } catch {
      // Silently fail — user can still proceed
    } finally {
      setLoadingContacts(false);
    }
  };

  // Contacts not yet linked to this client
  const availableContacts = useMemo(() => {
    const linkedIds = new Set(existingLinkedContacts.map((c: any) => c._id));
    return allContacts.filter((c: any) => !linkedIds.has(c._id));
  }, [allContacts, existingLinkedContacts]);

  // ── "Link Existing" handlers ──────────────────────────────────────────────

  const validateLinkForm = (): boolean => {
    const errs: Record<string, string> = {};
    if (!selectedContact) errs.contact = "Please select a contact";
    if (!linkForm.relationshipType) errs.relationshipType = "Required";
    setLinkErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const doLink = async (forcePrimary: boolean) => {
    setLoading(true);
    try {
      const payload = {
        clientId,
        contactId: selectedContact._id,
        relationshipType: linkForm.relationshipType,
        isPrimary: linkForm.isPrimary,
        orgId: login?.data?.orgId,
        userId: login?.data?.id,
        forcePrimary,
      };
      const resp: any = await linkContact(payload);
      if (resp.error) throw new Error(resp.error);
      updateContactGraph({ ...payload, _id: payload.contactId }).catch(() => {});
      window.dispatchEvent(new Event("contactsUpdated"));
      setAlert("Contact linked successfully!", "success");
      onSave();
      onClose();
    } catch (err: any) {
      setAlert(err.message || "Failed to link contact.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleLink = () => {
    if (!validateLinkForm()) return;
    if (linkForm.isPrimary) {
      const currentPrimary = existingLinkedContacts.find((c: any) => c.isPrimary);
      if (currentPrimary) {
        setExistingPrimaryName(currentPrimary.name || "Existing Contact");
        setConfirmSwapOpen(true);
        return;
      }
    }
    doLink(false);
  };

  // ── "Create New" handlers ─────────────────────────────────────────────────

  const updateNew = (field: string, value: string | boolean) => {
    setNewContact((prev) => ({ ...prev, [field]: value }));
    setNewErrors((prev) => { const n = { ...prev }; delete n[field]; return n; });
    if (createError) setCreateError(null);
  };

  const validateCreate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!newContact.firstName.trim()) errs.firstName = "Required";
    if (!newContact.surname.trim()) errs.surname = "Required";
    if (!newContact.email.trim()) errs.email = "Email is required";
    else if (!EMAIL_REGEX.test(newContact.email)) errs.email = "Enter a valid email address";
    if (newContact.phone && !PHONE_REGEX.test(newContact.phone.replace(/[\s\-()/]/g, "")))
      errs.phone = "Enter a valid phone number";
    if (!newContact.relationshipType) errs.relationshipType = "Required";
    setNewErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleCreate = async () => {
    if (!validateCreate()) return;
    setLoading(true);
    setCreateError(null);
    const fullName = `${newContact.firstName.trim()} ${newContact.surname.trim()}`.trim();
    try {
      // 1. Create contact
      const contactRes: any = await createContact({
        name: fullName,
        email: newContact.email.trim(),
        phone: newContact.phone.trim() || undefined,
        orgId: login?.data?.orgId,
        admin: { relationshipType: newContact.relationshipType },
      });

      // Handle 409 duplicate — surface inline, don't toast
      if (!contactRes.success) {
        if (contactRes.duplicate) {
          setCreateError(
            `A contact already exists with this email: ${contactRes.duplicate.name}` +
            (contactRes.duplicate.email ? ` (${contactRes.duplicate.email})` : "")
          );
        } else {
          setCreateError(contactRes.error || contactRes.message || "Failed to create contact");
        }
        return;
      }

      const contactId = contactRes.contact?._id || contactRes.contact?.id;
      if (!contactId) throw new Error("Contact was created but no ID was returned");

      // 2. Link to client
      const linkPayload = {
        clientId,
        contactId,
        relationshipType: newContact.relationshipType,
        isPrimary: newContact.isPrimary,
        orgId: login?.data?.orgId,
        userId: login?.data?.id,
        forcePrimary: newContact.isPrimary,
      };
      await linkContact(linkPayload);
      updateContactGraph({ ...linkPayload, _id: contactId }).catch(() => {});
      window.dispatchEvent(new Event("contactsUpdated"));
      setAlert("Contact created and linked successfully!", "success");
      onSave();
      onClose();
    } catch (err: any) {
      setCreateError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle
          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 0 }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LinkIcon color="primary" fontSize="small" />
            <Typography variant="h6" fontWeight={600} component="span">
              Add Contact — {clientName}
            </Typography>
            {entityType && (
              <Chip label={entityType} size="small" variant="outlined" sx={{ height: 20, fontSize: "0.7rem" }} />
            )}
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        {/* Tabs */}
        <Box sx={{ px: 3, borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab
              value="link"
              label="Link Existing"
              icon={<LinkIcon sx={{ fontSize: "1rem" }} />}
              iconPosition="start"
              sx={{ minHeight: 44, fontSize: "0.85rem", textTransform: "none" }}
            />
            <Tab
              value="create"
              label="Create New"
              icon={<PersonAddIcon sx={{ fontSize: "1rem" }} />}
              iconPosition="start"
              sx={{ minHeight: 44, fontSize: "0.85rem", textTransform: "none" }}
            />
          </Tabs>
        </Box>

        <DialogContent sx={{ pt: 2.5 }}>
          {/* ── Tab: Link Existing ─────────────────────────────────────────── */}
          {tab === "link" && (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Search and select an existing contact to link to <strong>{clientName}</strong>.
              </Typography>

              <Autocomplete
                options={availableContacts}
                loading={loadingContacts}
                getOptionLabel={(opt: any) => opt.name || ""}
                value={selectedContact}
                onChange={(_, val) => {
                  setSelectedContact(val);
                  setLinkErrors((prev) => { const n = { ...prev }; delete n.contact; return n; });
                }}
                filterOptions={(opts, { inputValue }) => {
                  const q = inputValue.toLowerCase();
                  return opts.filter(
                    (o: any) =>
                      o.name?.toLowerCase().includes(q) ||
                      o.email?.toLowerCase().includes(q)
                  );
                }}
                renderOption={(props, opt: any) => (
                  <Box component="li" {...props} key={opt._id}>
                    <Box>
                      <Typography variant="body2">{opt.name}</Typography>
                      {opt.email && (
                        <Typography variant="caption" color="text.secondary">{opt.email}</Typography>
                      )}
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search contacts"
                    size="small"
                    error={!!linkErrors.contact}
                    helperText={linkErrors.contact}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingContacts ? <CircularProgress size={14} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              <Collapse in={!!selectedContact} unmountOnExit>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: "8px", bgcolor: "background.default" }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Relationship for <strong>{selectedContact?.name}</strong>
                  </Typography>
                  <Stack spacing={1.5} pt={0.5}>
                    <FormControl fullWidth size="small" required error={!!linkErrors.relationshipType}>
                      <InputLabel>Relationship Type</InputLabel>
                      <Select
                        value={linkForm.relationshipType}
                        label="Relationship Type"
                        onChange={(e) => {
                          setLinkForm((p) => ({ ...p, relationshipType: e.target.value }));
                          setLinkErrors((p) => { const n = { ...p }; delete n.relationshipType; return n; });
                        }}
                      >
                        {config.relationships.map((r) => (
                          <MenuItem key={r} value={r}>{r}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={linkForm.isPrimary}
                          onChange={(e) => setLinkForm((p) => ({ ...p, isPrimary: e.target.checked }))}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2">Set as primary contact</Typography>
                      }
                    />
                  </Stack>
                </Paper>
              </Collapse>
            </Stack>
          )}

          {/* ── Tab: Create New ───────────────────────────────────────────── */}
          {tab === "create" && (
            <Stack spacing={1.75}>
              <Typography variant="body2" color="text.secondary">
                Create a new contact and link them to <strong>{clientName}</strong>.
              </Typography>

              {createError && (
                <Alert severity="error" sx={{ py: 0.5 }}>{createError}</Alert>
              )}

              <Stack direction="row" spacing={1.5}>
                <TextField
                  required label="First Name" fullWidth size="small"
                  value={newContact.firstName}
                  onChange={(e) => updateNew("firstName", e.target.value)}
                  error={!!newErrors.firstName} helperText={newErrors.firstName}
                  disabled={loading}
                />
                <TextField
                  required label="Last Name" fullWidth size="small"
                  value={newContact.surname}
                  onChange={(e) => updateNew("surname", e.target.value)}
                  error={!!newErrors.surname} helperText={newErrors.surname}
                  disabled={loading}
                />
              </Stack>

              <Stack direction="row" spacing={1.5}>
                <TextField
                  required label="Email" fullWidth size="small" type="email"
                  value={newContact.email}
                  onChange={(e) => updateNew("email", e.target.value)}
                  error={!!newErrors.email} helperText={newErrors.email}
                  disabled={loading}
                />
                <TextField
                  label="Phone" fullWidth size="small"
                  value={newContact.phone}
                  onChange={(e) => updateNew("phone", e.target.value)}
                  error={!!newErrors.phone} helperText={newErrors.phone || "Optional"}
                  disabled={loading}
                />
              </Stack>

              <FormControl fullWidth size="small" required error={!!newErrors.relationshipType}>
                <InputLabel>Relationship Type</InputLabel>
                <Select
                  value={newContact.relationshipType}
                  label="Relationship Type"
                  onChange={(e) => updateNew("relationshipType", e.target.value)}
                  disabled={loading}
                >
                  {config.relationships.map((r) => (
                    <MenuItem key={r} value={r}>{r}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Switch
                    checked={newContact.isPrimary}
                    onChange={(e) => updateNew("isPrimary", e.target.checked)}
                    size="small"
                    disabled={loading}
                  />
                }
                label={<Typography variant="body2">Set as primary contact</Typography>}
              />
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button onClick={onClose} variant="outlined" color="inherit" disabled={loading}>
            Cancel
          </Button>
          {tab === "link" ? (
            <Button
              onClick={handleLink}
              variant="contained"
              disabled={!selectedContact || !linkForm.relationshipType || loading}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : "Link Contact"}
            </Button>
          ) : (
            <Button
              onClick={handleCreate}
              variant="contained"
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : "Create & Link"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ── Primary contact swap confirmation ──────────────────────────────── */}
      <Dialog
        open={confirmSwapOpen}
        onClose={() => setConfirmSwapOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 600 }}>
          Change Primary Contact?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            The current primary contact is <strong>"{existingPrimaryName}"</strong>.
            Would you like to set <strong>"{selectedContact?.name}"</strong> as the new primary instead?
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
            The existing primary contact will be unset automatically.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button
            onClick={() => {
              setConfirmSwapOpen(false);
              setLinkForm((p) => ({ ...p, isPrimary: false }));
            }}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={() => { setConfirmSwapOpen(false); doLink(true); }}
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : "Confirm Change"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
